import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import OEContext from "../../layouts/OEContext";
import { axiosGet, axiosPost } from "../../data/helpers/baseApi";
import { SERVICE_URLS } from "../../data/helpers/serviceUrls";
import LoadingIndicator from "../../components/LoadingIndicator";
import FlexContributionLimitLabel from "../../components/FlexContributionLimitLabel";
import { useQueryGetFlexContributionLimits } from "../../data/services/flexBenefitsService";

const yesNoOptions = ["Yes", "No"];
const currentPlanYear = `${new Date().getFullYear() + 1}`;

const normalizeOption = (value, allowedOptions) => {
  if (value === null || value === undefined) {
    return "";
  }

  const normalizedValue = `${value}`.trim();
  if (!normalizedValue) {
    return "";
  }

  return allowedOptions.find((option) => option.toLowerCase() === normalizedValue.toLowerCase()) ?? "";
};

const pageStyle = {
  width: "100%",
  padding: "0 0 2rem",
  boxSizing: "border-box",
};

const contentStyle = {
  width: "100%",
  margin: 0,
  fontFamily: '"Segoe UI", Arial, sans-serif',
};

const formStyle = {
  width: "100%",
  padding: "20px 24px 24px",
  borderRadius: 0,
  border: "1px solid #d9d9d9",
  backgroundColor: "#fff",
  boxSizing: "border-box",
};

const sectionHeaderStyle = {
  marginBottom: 20,
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: 24,
  color: "#333",
};

const sectionSubtitleStyle = {
  margin: "0.4rem 0 0",
  fontSize: 14,
  color: "#666",
};

const formGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 20,
  alignItems: "start",
};

const sectionStyle = {
  width: "100%",
  padding: 0,
  boxSizing: "border-box",
};

const fullWidthSectionStyle = {
  ...sectionStyle,
  gridColumn: "1 / -1",
};

const contributionGroupStyle = {
  gridColumn: "1 / -1",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 20,
  padding: "18px 0 0",
  borderTop: "1px solid #e5e5e5",
  boxSizing: "border-box",
};

const contributionHeaderStyle = {
  gridColumn: "1 / -1",
};

const contributionTitleStyle = {
  margin: 0,
  fontSize: 18,
  color: "#333",
};

const contributionTextStyle = {
  margin: "0.4rem 0 0",
  fontSize: 14,
  color: "#666",
};

const labelStyle = {
  display: "block",
  fontWeight: 700,
  marginBottom: 8,
  color: "#333",
};

const controlStyle = {
  width: "100%",
  minHeight: 46,
  padding: "10px 12px",
  fontSize: 15,
  borderRadius: 4,
  border: "1px solid #c8c8c8",
  backgroundColor: "#fff",
  boxSizing: "border-box",
};

const notesStyle = {
  ...controlStyle,
  minHeight: 120,
  resize: "vertical",
};

const errorStyle = {
  marginBottom: 20,
  padding: 14,
  borderRadius: 4,
  border: "1px solid #d14343",
  backgroundColor: "#fff1f1",
  color: "#7a1010",
};

const successStyle = {
  marginBottom: 20,
  padding: 14,
  borderRadius: 4,
  border: "1px solid #2d8a39",
  backgroundColor: "#edf8ee",
  color: "#1f6f2b",
};

const validationTextStyle = {
  color: "#b42318",
  fontSize: 13,
  fontWeight: 600,
  marginTop: 8,
};

const buttonRowStyle = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: 24,
};

const primaryButtonStyle = {
  backgroundColor: "#0078d4",
  color: "#fff",
  border: "none",
  padding: "12px 24px",
  fontSize: 15,
  fontWeight: 600,
  borderRadius: 4,
  cursor: "pointer",
};

const formatOptionalNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return `${value}`;
};

const parseOptionalNumber = (value) => {
  if (value === "") {
    return null;
  }

  return Number(value);
};

const createInitialFormData = () => ({
  planYear: currentPlanYear,
  offerDca: "",
  hasDcaVendor: "",
  employerContributes: "",
  benefitGroupsEligibleForContribution: "",
  yearlyEmployerContributionAmount: "",
  isProrated: "",
  notes: "",
});

const createNoDataFormData = () => ({
  ...createInitialFormData(),
});

const hydrateFormData = (data) => ({
  planYear: currentPlanYear,
  offerDca: normalizeOption(data?.offerDca, yesNoOptions),
  hasDcaVendor: normalizeOption(data?.hasDcaVendor, yesNoOptions),
  employerContributes: normalizeOption(data?.employerContributes, yesNoOptions),
  benefitGroupsEligibleForContribution: data?.benefitGroupsEligibleForContribution ?? "",
  yearlyEmployerContributionAmount: formatOptionalNumber(data?.yearlyEmployerContributionAmount),
  isProrated: normalizeOption(data?.isProrated, yesNoOptions),
  notes: data?.notes ?? "",
});

const validateForm = (formData) => {
  const nextErrors = {};

  if (!formData.planYear) {
    nextErrors.planYear = "Plan year is required.";
  }

  if (!formData.offerDca) {
    nextErrors.offerDca = "This field is required.";
  }

  if (!formData.hasDcaVendor) {
    nextErrors.hasDcaVendor = "This field is required.";
  }

  if (!formData.employerContributes) {
    nextErrors.employerContributes = "This field is required.";
  }

  if (formData.employerContributes === "Yes" && !formData.yearlyEmployerContributionAmount) {
    nextErrors.yearlyEmployerContributionAmount = "Contribution amount is required when the company contributes to the DCA.";
  }

  return nextErrors;
};

const getSaveSuccessMessage = (benefitLabel) => `${benefitLabel} details were saved successfully.`;

const EmployerDCAForm = ({
  clientId: clientIdProp,
  oePeriodId: oePeriodIdProp,
  planOptions = [],
}) => {
  const routeParams = useParams();
  const clientId = clientIdProp ?? routeParams.clientId;
  const oePeriodId = oePeriodIdProp ?? routeParams.oePeriodId;
  const oePeriod = React.useContext(OEContext);
  const effectiveOePeriodId = oePeriod?.oePeriodId ?? oePeriodId;
  const { data: flexLimitsResponse } = useQueryGetFlexContributionLimits(
    effectiveOePeriodId,
  );
  const flexLimits = flexLimitsResponse?.data;
  const initialFormData = useMemo(() => createInitialFormData(), []);

  const [isLoading, setIsLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    let isMounted = true;

    const loadDcaData = async () => {
      if (!clientId || !effectiveOePeriodId) {
        setErrorMessage("DCA context is missing. Please reload this page from the rate model flow.");
        setFormData(initialFormData);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setSuccessMessage("");
      setErrorMessage("");

      try {
        const response = await axiosGet(
          SERVICE_URLS.CLIENT_DCA_MATCH(effectiveOePeriodId, clientId),
        );

        if (isMounted && response?.status === 200 && response.data) {
          setFormData(hydrateFormData(response.data));
        } else if (isMounted && response?.status === 204) {
          setFormData(createNoDataFormData());
        } else if (isMounted) {
          setFormData(initialFormData);
        }
      } catch {
        if (isMounted) {
          setFormData(initialFormData);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadDcaData();

    return () => {
      isMounted = false;
    };
  }, [clientId, effectiveOePeriodId, initialFormData]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previousFormData) => {
      const nextFormData = {
        ...previousFormData,
        [name]: value,
      };

      if (name === "employerContributes" && value !== "Yes") {
        nextFormData.benefitGroupsEligibleForContribution = "";
        nextFormData.yearlyEmployerContributionAmount = "";
        nextFormData.isProrated = "";
      }

      return nextFormData;
    });

    setValidationErrors((previousErrors) => {
      if (!previousErrors[name] && !(name === "employerContributes" && previousErrors.yearlyEmployerContributionAmount)) {
        return previousErrors;
      }

      const nextErrors = { ...previousErrors };
      delete nextErrors[name];

      if (name === "employerContributes" && value !== "Yes") {
        delete nextErrors.yearlyEmployerContributionAmount;
      }

      return nextErrors;
    });

    if (successMessage) {
      setSuccessMessage("");
    }

    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!clientId || !effectiveOePeriodId) {
      setErrorMessage("DCA context is missing. Please reload this page from the rate model flow.");
      return;
    }

    const numericOePeriodId = Number(effectiveOePeriodId);
    if (!Number.isFinite(numericOePeriodId)) {
      setErrorMessage("OE period could not be determined. Please reload the page.");
      return;
    }

    const nextValidationErrors = validateForm(formData);
    setValidationErrors(nextValidationErrors);
    if (Object.keys(nextValidationErrors).length > 0) {
      return;
    }

    setIsLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const result = await axiosPost(
        SERVICE_URLS.CLIENT_DCA_MATCH(numericOePeriodId, clientId),
        {
          oePeriodId: numericOePeriodId,
          clientId,
          planYear: currentPlanYear,
          offerDca: normalizeOption(formData.offerDca, yesNoOptions) || null,
          hasDcaVendor: normalizeOption(formData.hasDcaVendor, yesNoOptions) || null,
          employerContributes: normalizeOption(formData.employerContributes, yesNoOptions) || null,
          benefitGroupsEligibleForContribution:
            formData.employerContributes === "Yes"
              ? formData.benefitGroupsEligibleForContribution || null
              : null,
          yearlyEmployerContributionAmount:
            formData.employerContributes === "Yes"
              ? parseOptionalNumber(formData.yearlyEmployerContributionAmount)
              : null,
          isProrated:
            formData.employerContributes === "Yes"
              ? normalizeOption(formData.isProrated, yesNoOptions) || null
              : null,
          notes: formData.notes || null,
        }
      );

      if (result?.status === 200 || result?.status === 201) {
        let hydratedData = null;

        if (result?.data && typeof result.data === "object" && !Array.isArray(result.data)) {
          hydratedData = result.data;
        } else {
          try {
            const refresh = await axiosGet(
              SERVICE_URLS.CLIENT_DCA_MATCH(numericOePeriodId, clientId),
            );

            if (refresh?.status === 200 && refresh.data) {
              hydratedData = refresh.data;
            }
          } catch {
            // Keep the form values the user entered if re-fetch fails.
          }
        }

        if (hydratedData) {
          setFormData(hydrateFormData(hydratedData));
        }

        setValidationErrors({});
        setSuccessMessage(getSaveSuccessMessage("DCA"));
      } else {
        setErrorMessage(
          "We couldn't save the DCA details right now. Please review the information and try again."
        );
      }
    } catch (error) {
      setErrorMessage(
        "We couldn't save the DCA details right now. Please review the information and try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderValidationError = (fieldName) => {
    if (!validationErrors[fieldName]) {
      return null;
    }

    return <div style={validationTextStyle}>{validationErrors[fieldName]}</div>;
  };

  return (
    <div style={pageStyle}>
      <div style={contentStyle}>
        <div style={sectionHeaderStyle}>
          <div>
            <h2 style={sectionTitleStyle}>DCA - Dependent Care Account</h2>
                <p style={{ ...sectionSubtitleStyle, width: 620 }}>
              Enter the DCA setup details for this client. Additional contribution fields appear only when needed.
            </p>
            <div>
              <FlexContributionLimitLabel label="DCA" amount={flexLimits?.dca} />
            </div>
            <p>
            <div style={labelStyle}>DCA Contribution Awareness Notice</div>
            <p style={{ ...sectionSubtitleStyle, width: 620 }}>
              Employers may elect to offer and fund Dependent Care Assistance (DCA) contributions 
              as part of their benefits strategy. DCA participation, eligibility, contribution limits, 
              tax filing status considerations, and plan provisions are subject to applicable IRS regulations 
              and plan document requirements. For 2027, the annual contribution limit is $7,500 for employees 
              filing Single or Married Filing Jointly and $3,750 for employees filing Married Filing Separately. 
              Employer contributions count toward the applicable IRS maximum and should be considered when 
              establishing a DCA funding strategy. In all cases, the applicable IRS regulations and plan 
              documents will govern. If you have questions about DCA participation or contribution elections, 
              please contact your dedicated PrestigePEO Benefits Specialist or Business Development Manager. 
              This notice does not constitute tax, legal, or financial advice and employers should consult a 
              qualified advisor regarding their individual circumstances.
              </p>
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          {successMessage ? <div style={successStyle}>{successMessage}</div> : null}
          {errorMessage ? <div style={errorStyle}>{errorMessage}</div> : null}

          <div style={formGridStyle}>
            <div style={sectionStyle}>
              <label htmlFor="planYear" style={labelStyle}>
                DCA plan year
              </label>
              <input
                type="text"
                id="planYear"
                name="planYear"
                value={formData.planYear}
                onChange={handleChange}
                readOnly
                style={controlStyle}
              />
              {renderValidationError("planYear")}
            </div>

            <div style={sectionStyle}>
              <label htmlFor="offerDca" style={labelStyle}>
                Does client offer DCA?
              </label>
              <select
                id="offerDca"
                name="offerDca"
                value={formData.offerDca}
                onChange={handleChange}
                style={controlStyle}
              >
                <option value="">Select...</option>
                {yesNoOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {renderValidationError("offerDca")}
            </div>

            <div style={sectionStyle}>
              <label htmlFor="hasDcaVendor" style={labelStyle}>
                Company currently has a DCA vendor?
              </label>
              <select
                id="hasDcaVendor"
                name="hasDcaVendor"
                value={formData.hasDcaVendor}
                onChange={handleChange}
                style={controlStyle}
              >
                <option value="">Select...</option>
                {yesNoOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {renderValidationError("hasDcaVendor")}
            </div>

            <div style={sectionStyle}>
              <label htmlFor="employerContributes" style={labelStyle}>
                Does the company contribute to the DCA?
              </label>
              <select
                id="employerContributes"
                name="employerContributes"
                value={formData.employerContributes}
                onChange={handleChange}
                style={controlStyle}
              >
                <option value="">Select...</option>
                {yesNoOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {renderValidationError("employerContributes")}
            </div>

            {formData.employerContributes === "Yes" && (
              <div style={contributionGroupStyle}>
                <div style={contributionHeaderStyle}>
                  <h4 style={contributionTitleStyle}>Employer contribution settings</h4>
                  <p style={contributionTextStyle}>
                    These fields appear only when the company contributes to the DCA.
                  </p>
                </div>

                <div style={sectionStyle}>
                  <label htmlFor="yearlyEmployerContributionAmount" style={labelStyle}>
                    Yearly employer contribution amount
                  </label>
                  <input
                    type="number"
                    id="yearlyEmployerContributionAmount"
                    name="yearlyEmployerContributionAmount"
                    value={formData.yearlyEmployerContributionAmount}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="Enter amount"
                    style={controlStyle}
                  />
                  {renderValidationError("yearlyEmployerContributionAmount")}
                </div>

                <div style={sectionStyle}>
                  <label htmlFor="isProrated" style={labelStyle}>
                    Is the contribution amount pro-rated?
                  </label>
                  <select
                    id="isProrated"
                    name="isProrated"
                    value={formData.isProrated}
                    onChange={handleChange}
                    style={controlStyle}
                  >
                    <option value="">Select...</option>
                    {yesNoOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div style={fullWidthSectionStyle}>
              <label htmlFor="notes" style={labelStyle}>
                DCA notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                maxLength={255}
                rows={4}
                style={notesStyle}
              />
            </div>
          </div>

          <div style={buttonRowStyle}>
            <button type="submit" style={primaryButtonStyle}>Submit</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployerDCAForm;
